import React from 'react';

import { VideoCard } from 'components/VideoCard';
import { Accordion } from 'components/Accordion';
import { sprintf } from 'sprintf';

type Props = {};

const FAQS = [
  {
    title: 'Why has Sedition launched NFTs?',
    content: sprintf(
      `
        <p>Sedition is the perfect platform for NFTs.</p>
        <p>In fact, we kind of invented our own version before NFTs existed back in 2011. Back then, the blockchain technology was at its very infancy, thus artworks on Sedition had to be centralised. Ever since then, Sedition has been looking for other ways in which artwork ownership can be tracked.</p>
        <p>We are more confident than ever that blockchain technology will become the go-to tech for underpinning records - not just in the arts industry, and not just in the digital world, but across the board. Blockchain allows for ownership to be tracked independently of any centralised entity, such as Sedition.</p>
        <p>Blockchain is an ideal technology for our purposes, and Sedition is delighted to start making use of it.</p>
      `,
    ),
  },
  {
    title: 'What are the benefits of minting my Sedition artwork as an NFT?',
    content: sprintf(
      `
        <p>There are numerous benefits to listing your artwork as an NFT.</p>
        <p>
          <b>The work is yours. Period</b><br />
          After your artwork is minted as an NFT, it is attached to your crypto wallet. No one can take it away from you. Even if Sedition were to be removed from the planet by a band of vengeful aliens, the artwork is truly and forever yours.
        </p>
        <p>
          <b>You can resell your artwork</b><br />
          By taking your artwork out of the Sedition universe, you can resell it on a secondary NFT marketplace such as OpenSea. You can put any price you like on it - you can mint your edition as an NFT and list it for 10 dollars or 10 million dollars - the world is your oyster!
        </p>
        <p>
          <b>Ownership is transparent and traceable</b><br />
          NFTs are stored on the blockchain where they are tracked and verified by the network. The ownership of an NFT is transparent and can be verified by anyone on the blockchain.
        </p>
        <p>
          <b>Crypto exposure</b><br />
          Minting the work as an NFT lists it on the blockchain with a certain value. The value is then underpinned in cryptocurrency - in this case ether. You are then exposed to the currency price movements that underpin your NFT - this can be both a risk or an advantage. If ether value rises, so does the price of your NFT - and vice-versa.
        </p>
      `,
    ),
  },
  {
    title: 'What are the risks of minting my Sedition artwork as an NFT?',
    content: sprintf(
      `
        <p>Anything you do bears risk, and listing your artwork on the blockchain is no different.</p>
        <p>
          <b>With true ownership, comes responsibility</b><br />
          Blockchain technology is brilliantly giving people the possibility to truly own their digital assets without relying on a centralised entity. However, with true ownership comes a lot of responsibility. If you lose access to your crypto wallet, you lose access to your NFT. NO ONE can recover it for you.
        </p>
        <p>
          <b>Transactions are irreversible</b><br />
          Blockchain transactions are irreversible by design, therefore no one, not even the creator of the chain can undo them. This means that if you send your NFT to a wrong address by mistyping it, it’s gone forever.
        </p>
        <p>
          <b>Increased need for security</b><br />
          Criminals and scammers want your stuff - and they want your NFTs. Since blockchain transactions are irreversible and anonymised, crypto became a perfect vehicle for criminals to target. You do need to take security very seriously when dealing with crypto and NFTs, otherwise if someone comes into your wallet, they can and they will help themselves.
        </p>
        <p>
          <b>Crypto exposure</b><br />
          Minting the work as an NFT lists it on the blockchain with a certain value. The value is then underpinned in cryptocurrency - in this case ether. You are then exposed to the currency price movements that underpin your NFT - this can be both a risk or an advantage. If ether value rises, so does the price of your NFT - and vice-versa.
        </p>
      `,
    ),
  },
  {
    title: 'What NFT-related fees does Sedition take?',
    content: sprintf(
      `
        <p>Sedition takes a small administrative fee for listing the artwork as an NFT.</p>
        <p>The current fee that Sedition takes can be seen after clicking the “Create NFT on Blockchain” button from the Vault.</p>
        <p>If you mint a Sedition NFT and choose to resell it, every time the artwork changes hands, a secondary trade fee will be charged. The fee is split between Sedition and the artist who created the artwork in the first place.</p>
        <p>The fee is set at 7.5%% of the resale value of the artwork. Please note that in addition to that, OpenSea charges a 2.5%% fee for every trade that takes place. The total fee is thus 10%%.</p>
      `,
    ),
  },
  {
    title:
      'I have concerns that NFTs are causing climate change. Is it true? What does Sedition think about it?',
    content: sprintf(
      `
        <p>No, NFTs can no longer be considered a major polluter.</p>
        <p>Climate change is of grave concern to many artists and collectors in the Sedition community, and of course the Sedition team. We take these issues very seriously and it is for this reason that we have waited until now to enter the NFT space. Indeed, it is a known fact that the proof-of-work consensus mechanism that was previously utilized by the Ethereum blockchain consumed enormous amounts of energy. Producing this much electricity did inevitably cause emissions.</p>
        <p>However, <b>Ethereum has switched to an entirely different consensus algorithm in September 2022, called proof-of-stake</b>. That algorithm has reduced the energy consumption of the Ethereum network by more than 99%%, making Ethereum virtually carbon neutral.</p>
        <p><b>At Sedition we were making a very limited use of the Ethereum blockchain until we were satisfied with the underlying technology - and we are now satisfied that the shift to proof-of-stake is complete.</b></p>
      `,
    ),
  },
];

export const FAQSection: React.FC<Props> = () => {
  return (
    <div className='container container--video-tour d-flex flex-column'>
      <div className='mb-2'>
        <VideoCard
          videoId='753741679'
          videoTitle={'How to Turn Sedition Artworks into NFTs'}
          videoUrl='nft-landing-page/video-preview.mp4'
          videoThumbnail='nft-landing-page/video-preview.jpg'
          videoButtonText={'Learn how to create Sedition NFTs'}
          buttonText={'Learn more in our Help Centre'}
          cardTitle={'How to turn Sedition Artworks into NFTs'}
          cardText={
            'Look for artworks marked with an NFT icon and purchase them as you normally would. Then follow a few easy steps in your Vault to transfer the artwork to the Ethereum blockchain.'
          }
        />
      </div>
      <div className='mb-3'>
        <h1 className='headline headline--medium text-left mb-2 mb-sm-3'>
          {'Common NFT Questions'}
        </h1>
        {FAQS.map(({ title, content }) => (
          <Accordion title={title} key={`item-${title}`} content={content} />
        ))}
      </div>
      <div>
        <p
          className='standfirst--large text-center'
          dangerouslySetInnerHTML={{
            __html: sprintf(
              `For more information visit our %s`,
              `<a href="${Routes.learn_more_index_path()}" class="link-border">Help Centre</a>`,
            ),
          }}
        ></p>
      </div>
    </div>
  );
};
